import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Plane, MapPin, ArrowRight, Navigation, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Map, useMap } from "@vis.gl/react-google-maps";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIRPORT_COORDS = { lat: 13.1986, lng: 77.7066 };

const airportRoutes = [
  { id: "blr-koramangala", name: "Koramangala", address: "Koramangala, Bengaluru", coords: { lat: 12.9352, lng: 77.6245 } },
  { id: "blr-indiranagar", name: "Indiranagar", address: "Indiranagar, Bengaluru", coords: { lat: 12.9784, lng: 77.6408 } },
  { id: "blr-whitefield", name: "Whitefield", address: "Whitefield, Bengaluru", coords: { lat: 12.9698, lng: 77.7499 } },
  { id: "blr-mgroad", name: "MG Road", address: "MG Road, Bengaluru", coords: { lat: 12.9758, lng: 77.6045 } },
  { id: "blr-electronic-city", name: "Electronic City", address: "Electronic City, Bengaluru", coords: { lat: 12.8399, lng: 77.6770 } },
  { id: "blr-jayanagar", name: "Jayanagar", address: "Jayanagar, Bengaluru", coords: { lat: 12.9300, lng: 77.5838 } },
  { id: "blr-hsr", name: "HSR Layout", address: "HSR Layout, Bengaluru", coords: { lat: 12.9116, lng: 77.6389 } },
  { id: "blr-marathahalli", name: "Marathahalli", address: "Marathahalli, Bengaluru", coords: { lat: 12.9591, lng: 77.7009 } },
];

const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#1a1a1a" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b6b6b" }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#2a2a2a" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#1a1a1a" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#333333" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e0e0e" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3a3a3a" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#1a1a1a" }] },
  { featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];

const createSVGMarker = (type) => {
  const svg = type === "airport"
    ? `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36"><circle cx="18" cy="18" r="16" fill="%23F97316" stroke="white" stroke-width="3"/><path d="M18 10l-2 6h-5l4 3-1.5 5L18 21l4.5 3-1.5-5 4-3h-5z" fill="white"/></svg>`
    : `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30"><circle cx="15" cy="15" r="13" fill="%23F97316" stroke="white" stroke-width="3"/><circle cx="15" cy="15" r="4" fill="white"/></svg>`;
  return `data:image/svg+xml,${svg}`;
};

const MapMarkers = ({ destination }) => {
  const map = useMap();
  const airportMarkerRef = useRef(null);
  const destMarkerRef = useRef(null);
  const directionsRendererRef = useRef(null);

  useEffect(() => {
    if (!map || !window.google) return;

    if (!airportMarkerRef.current) {
      airportMarkerRef.current = new window.google.maps.Marker({
        position: AIRPORT_COORDS,
        map,
        icon: {
          url: createSVGMarker("airport"),
          scaledSize: new window.google.maps.Size(36, 36),
          anchor: new window.google.maps.Point(18, 18),
        },
        title: "Bangalore International Airport",
      });
    }

    return () => {
      if (airportMarkerRef.current) {
        airportMarkerRef.current.setMap(null);
        airportMarkerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    if (!map || !window.google) return;

    if (destMarkerRef.current) {
      destMarkerRef.current.setMap(null);
      destMarkerRef.current = null;
    }
    if (directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = null;
    }

    if (!destination) {
      map.setCenter(AIRPORT_COORDS);
      map.setZoom(11);
      return;
    }

    destMarkerRef.current = new window.google.maps.Marker({
      position: destination.coords,
      map,
      icon: {
        url: createSVGMarker("dest"),
        scaledSize: new window.google.maps.Size(30, 30),
        anchor: new window.google.maps.Point(15, 15),
      },
      title: destination.name,
    });

    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#F97316",
        strokeWeight: 4,
        strokeOpacity: 0.9,
      },
    });
    directionsRendererRef.current.setMap(map);

    const service = new window.google.maps.DirectionsService();
    service.route(
      {
        origin: AIRPORT_COORDS,
        destination: destination.coords,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK" && directionsRendererRef.current) {
          directionsRendererRef.current.setDirections(result);
          const bounds = result.routes[0].bounds;
          map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
        }
      }
    );

    return () => {
      if (destMarkerRef.current) {
        destMarkerRef.current.setMap(null);
        destMarkerRef.current = null;
      }
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
        directionsRendererRef.current = null;
      }
    };
  }, [map, destination]);

  return null;
};

const PricingSection = ({ onBookNow }) => {
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [liveFare, setLiveFare] = useState(null);
  const [fareLoading, setFareLoading] = useState(false);
  const selectedFare = airportRoutes.find(r => r.id === selectedRoute) || null;

  useEffect(() => {
    if (!selectedRoute) {
      setLiveFare(null);
      return;
    }
    const route = airportRoutes.find(r => r.id === selectedRoute);
    if (!route) return;

    setFareLoading(true);
    axios.post(`${API}/pricing`, {
      pickup_address: route.address,
      drop_address: "Terminal 1, Kempegowda International Airport",
      pickup_time: "10:00 AM",
    }).then(resp => {
      setLiveFare(resp.data);
    }).catch(() => {
      setLiveFare(null);
    }).finally(() => {
      setFareLoading(false);
    });
  }, [selectedRoute]);

  return (
    <section id="pricing" className="py-16 sm:py-20 bg-sarathi-background">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <span className="text-sarathi-primary font-semibold text-xs tracking-widest uppercase">
            Transparent Pricing
          </span>
          <h2 className="font-outfit text-2xl sm:text-3xl font-bold tracking-tight text-white mt-3 mb-3">
            Simple, Honest Fares
          </h2>
          <p className="text-sarathi-text-secondary text-sm max-w-xl mx-auto">
            No hidden charges. No surge pricing. Just transparent fares.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left - Fare Card */}
            <div className="bg-sarathi-surface border border-white/10 rounded-3xl p-6 sm:p-8 hover:border-sarathi-primary/30 transition-all">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-sarathi-primary/20 rounded-2xl flex items-center justify-center">
                  <Plane className="w-7 h-7 text-sarathi-primary" />
                </div>
                <div>
                  <h3 className="font-outfit text-xl font-bold text-white">Airport Transfer</h3>
                  <p className="text-sarathi-text-secondary text-sm">Bangalore International Airport ↔ City</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sarathi-text-muted text-sm mb-3">Select your destination</label>
                <Select onValueChange={setSelectedRoute} data-testid="route-selector">
                  <SelectTrigger className="w-full bg-sarathi-background border-white/10 text-white rounded-xl h-14 text-base" data-testid="route-selector-trigger">
                    <MapPin className="w-5 h-5 mr-3 text-sarathi-primary" />
                    <SelectValue placeholder="Choose area" />
                  </SelectTrigger>
                  <SelectContent className="bg-sarathi-surface border-white/10">
                    {airportRoutes.map((route) => (
                      <SelectItem
                        key={route.id}
                        value={route.id}
                        className="text-white hover:bg-sarathi-elevated text-base py-3"
                      >
                        {route.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {fareLoading && (
                <div className="flex items-center justify-center gap-2 py-8 text-sarathi-text-muted text-sm">
                  <Loader2 className="w-5 h-5 animate-spin text-sarathi-primary" />
                  Calculating fare...
                </div>
              )}

              {selectedFare && liveFare && liveFare.success && !fareLoading && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-sarathi-background rounded-2xl p-6 mb-6 border border-sarathi-primary/30"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-sarathi-text-muted text-sm">Estimated Fare</p>
                      <p className="font-outfit text-4xl font-bold text-sarathi-primary" data-testid="fare-amount">
                        {liveFare.fare.displayTotal}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sarathi-text-muted text-sm">Distance</p>
                      <p className="text-white font-semibold text-lg" data-testid="fare-distance">{liveFare.distanceKm} km</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sarathi-text-secondary text-sm mb-3">
                    <MapPin className="w-4 h-4 text-sarathi-primary" />
                    <span>Bangalore International Airport ↔ {selectedFare.name}</span>
                  </div>
                  {/* Fare breakdown */}
                  <div className="border-t border-white/10 pt-3 mt-1 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-sarathi-text-muted">Base fare ({liveFare.fare.zone || "Outstation"})</span>
                      <span className="text-white">₹{liveFare.fare.baseFare.toLocaleString()}</span>
                    </div>
                    {liveFare.fare.toll > 0 && (
                      <div className="flex justify-between">
                        <span className="text-sarathi-text-muted">Tolls</span>
                        <span className="text-white">₹{liveFare.fare.toll.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-sarathi-text-muted">GST (CGST + SGST)</span>
                      <span className="text-white">₹{liveFare.fare.gst.toLocaleString()}</span>
                    </div>
                  </div>
                </motion.div>
              )}

              <div className="bg-sarathi-background/50 rounded-xl p-5 mb-6">
                <p className="text-white font-semibold text-sm mb-3">Includes:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sarathi-text-secondary text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-sarathi-primary rounded-full"></div>
                    <span>Confirmed ride, no cancellations</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-sarathi-primary rounded-full"></div>
                    <span>Clean, comfortable MG Windsor EV</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-sarathi-primary rounded-full"></div>
                    <span>Courteous, dependable driver</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-sarathi-primary rounded-full"></div>
                    <span>A fresh snack and beverage for your journey.</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={onBookNow}
                className="w-full bg-sarathi-primary hover:bg-sarathi-primary-hover text-white rounded-xl h-14 text-base font-semibold"
                data-testid="pricing-airport-btn"
              >
                Book Airport Transfer
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <p className="text-sarathi-text-muted text-xs text-center mt-4">
                * All fares include GST. Tolls and parking extra if applicable.
              </p>
            </div>

            {/* Right - Google Map */}
            <div className="bg-sarathi-surface border border-white/10 rounded-3xl overflow-hidden relative" data-testid="pricing-map-container">
              <div className="h-full min-h-[400px] lg:min-h-0">
                  <Map
                    style={{ width: "100%", height: "100%", minHeight: "400px" }}
                    defaultCenter={AIRPORT_COORDS}
                    defaultZoom={11}
                    gestureHandling="greedy"
                    disableDefaultUI={true}
                    zoomControl={true}
                    styles={darkMapStyles}
                  >
                    <MapMarkers destination={selectedFare} />
                  </Map>

                {!selectedFare && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                    <div className="text-center px-6">
                      <Navigation className="w-10 h-10 text-sarathi-primary mx-auto mb-3 opacity-80" />
                      <p className="text-white font-medium text-sm">Select a destination to see the route</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
